import AjaxLoader from '../ajax-loader/ajax-loader';
import { context as defaultContext } from './mini-cart-defaults';
import template from './templates/mini-cart';
import store from '@stores/cart/cart-store';
import MiniCartLine from './mini-cart-line';
import forceAsync from '@utils/force-async';
import * as miniCartDatalayer from '@datalayer/components/mini-cart-datalayer';

const CLASS_NAME_IS_OVERFLOW = '-is-overflow';

const DATA_ATTRIBUTE_REFERENCE = 'data-ref';
const DATA_ATTRIBUTE_REGION = 'data-region';

const DEFAULT_OPTIONS = {
    ariaLabelElementId: '',
    replaceOriginEl: false
};

const LINE_ATTRIBUTE_VALUE = 'line';

const LINE_REFERENCE_SELECTOR = `[${DATA_ATTRIBUTE_REFERENCE}=${LINE_ATTRIBUTE_VALUE}]`;
const LINE_REGION_SELECTOR = `[${DATA_ATTRIBUTE_REGION}=${LINE_ATTRIBUTE_VALUE}]`;

const SCROLL_DURATION = 250;

export default class MiniCart {
    constructor($originEl, context, options) {
        options = { ...DEFAULT_OPTIONS, ...options};

        this.context = { ...defaultContext, ...context };

        const $parsed = $( $.parseHTML(template) );
        this.$el = $parsed;
        this.$originEl = $originEl;

        this.$emptyLabel = this.$el.find('[data-label="empty"]');
        this.$subtotalLabel = this.$el.find('[data-label="subtotal"]');

        this.$checkout = this.$el.find('[data-region="checkout"]');
        this.$empty = this.$el.find('[data-region="empty"]');
        this.$items = this.$el.find('[data-region="items"]');
        this.$lines = this.$el.find('[data-region="lines"]');
        this.$price = this.$el.find('[data-region="price"]');
        this.$loader = this.$el.find('[data-region="loader"]');
        this.$scroll = this.$el.find('[data-region="scroll"]');
        this.$upsell = this.$el.find('[data-region="upsell"]');

        this.$lineRef = this.$el.find(LINE_REFERENCE_SELECTOR);

        this.$priceLoaderPlaceholder = this.$el.find('[data-placeholder="priceLoader"]');

        this.isOverflow = false;
        this.loader = null;
        this.priceLoader = null;

        this.lines = [];
        this.lineItemNode = this.$lineRef.removeAttr(DATA_ATTRIBUTE_REFERENCE).get(0) || document.createElement('li');

        this.ariaLabelElementId = options.ariaLabelElementId;
        this.replaceOriginEl = options.replaceOriginEl;
    }

    get itemsText() {
        return this.context.Items.replace('{0}', store.getters.linesCount);
    }

    async init() {
        this.$lineRef.remove();

        this.renderLabels();
        this.renderLinks();
        this.setupAccessibility();
        this.setupLoaders();
        await this.loadCart();

        this.setupListeners();
    }

    checkForScroll() {
        this.isOverflow = this.$scroll.get(0).scrollHeight > this.$scroll.height();

        if(this.isOverflow) {
            this.$el.addClass(CLASS_NAME_IS_OVERFLOW);
        } else {
            this.$el.removeClass(CLASS_NAME_IS_OVERFLOW);
        }
    }

    focus(productInfo) {
        const { productId, variantId } = productInfo || {};
        const index = store.state.rawLines.findIndex((line) => line.Product?.ProductId === productId && line.Product?.ProductVariantId === variantId);

        let focusEl = this.$checkout.get(0);

        if(index > -1) {
            focusEl = this.lines[index]?.$link?.get(0);
            if(focusEl) {
                forceAsync(() => {
                    if(this.isOverflow) {
                        this.$scroll.scrollTop(focusEl.offsetTop);
                    }
                });
            }
        }
        var quickshop = document.getElementById('pdpQuickview');
        if(!!quickshop){
            focusEl?.focus();
        }else {
            $('html,body').animate({ scrollTop: 0 }, SCROLL_DURATION, () => {
                focusEl?.focus();
            });
        }
    }

    async loadCart() {
        if(!store.state.loaded) {
            const unsubscribe = store.watch('loaded', (...[,,loaded]) => {
                if(loaded) {
                    this.onCartLoaded();
                    unsubscribe();
                }
            });

            await store.actions.getCart();
        } else {
            this.onCartLoaded();
        }
    }

    onCartLoaded() {
        this.toggleEmptyCart();
        this.setupLines();
        this.renderLines();
        this.updateLineCount();
        this.updatePrice();
        this.updateLoadingState();
        this.updateUpsell();

        this.loader.active = false;
        this.$loader.hide();
    }

    removeDeletedLines(deletedLines = []) {
        const deleteAnimation = deletedLines.map((component) => {
            const $line = component.$el.closest(LINE_REGION_SELECTOR);
            $line.css('position', 'relative');

            return new Promise((resolve) => {
                $line.animate({ opacity: 0, right: $line.width() }, 'fast', () => {
                    $line.slideUp('fast', () => {
                        $line.remove();
                        resolve();
                    });
                });
            });
        });

        return new Promise((resolve) => {
            Promise.all(deleteAnimation).then(resolve);
        });
    }

    render() {
        if(this.$originEl) {
            if(this.replaceOriginEl) {
                this.$originEl.replaceWith(this.$el);
            } else {
                this.$originEl.html(this.$el);
            }
        }

        return this.$el;
    }

    renderAddedLines() {
        store.getters.linesAdded.forEach((id) => {
            const index = store.getters.lineIds.indexOf(id);
            const prevIndex = index - 1;
            const component = this.lines[index];
            const lineItem = this.lineItemNode.cloneNode(false);

            component.init();
            lineItem.appendChild(component.$el.get(0));

            if (prevIndex > -1) {
                const prevComponent = this.lines[prevIndex];
                const $line = prevComponent.$el.closest(LINE_REGION_SELECTOR);
                $line.after(lineItem);
            } else {
                this.$lines.prepend(lineItem);
            }
        });
    }

    renderLabels() {
        this.$emptyLabel.html(this.context.YourShoppingCartEmpty);
        this.$items.html(this.itemsText);
        this.$subtotalLabel.html(this.context.CartSubtotal);
    }

    renderLines() {
        if(this.lines.length) {
            const fragment = document.createDocumentFragment();

            this.lines.forEach((component) => {
                const lineItem = this.lineItemNode.cloneNode(false);

                lineItem.appendChild(component.$el.get(0));
                fragment.appendChild(lineItem);
            });

            this.$lines.html(fragment);

            this.showLines();
        }
    }

    renderLinks() {
        if(this.context.CheckoutLink?.Url) {
            this.$checkout
                .attr('href', this.context.CheckoutLink.Url)
                .text(this.context.CheckoutLink.Text);
        } else {
            this.$checkout.hide();
        }
    }

    setupAccessibility() {
        this.$el.attr('aria-live', 'polite');
        if(this.ariaLabelElementId) {
            this.$lines.attr('aria-labelledby', this.ariaLabelElementId);
        }
    }

    setupLines() {
        store.getters.lines.forEach(line => {
            const component = new MiniCartLine(null, this.context, line);
            component.init();

            this.lines.push(component);
        });
    }

    setupListeners() {
        this.$checkout.on('click', () => miniCartDatalayer.sendCheckoutClick());

        store.watch('formattedMiniCartTotal', () => this.updatePrice());
        store.watch('freeShippingText', () => this.updateUpsell());
        store.watch('loadingLine', () => this.updateLoadingState());
        store.watch('rawLines', () => this.updateLineState());
    }

    setupLoaders() {
        this.loader = new AjaxLoader(this.$loader, {
            aria: false,
            replaceOriginEl: false,
            size: 'sm',
            visible: true
        });

        this.priceLoader = new AjaxLoader(this.$priceLoaderPlaceholder, {
            aria: false,
            size: 'xs',
            visible: true
        });
    }

    showLines() {
        if(this.$el.is(':visible')) {
            this.$lines.slideDown('fast', () => {
                this.checkForScroll();
            });
        } else {
            this.$lines.css('display', 'block');
            forceAsync(() => this.checkForScroll());
        }
    }

    toggleEmptyCart() {
        if(store.getters.isEmpty) {
            this.$empty.show();
        } else {
            this.$empty.hide();
        }
    }    

    updateLineCount() {
        this.$items.html(this.itemsText);
    }

    updateUpsell() {
        if(store.state.freeShippingText) {
            this.$upsell.show().html(store.state.freeShippingText);
        } else {
            this.$upsell.hide();
        }
    }

    async updateLines() {
        const deletedLines = store.getters.linesRemoved.map((id) => {
            const index = store.state.cachedLineIds.indexOf(id);
            return this.lines[index];
        });

        this.lines =
            store.getters.lines
                .map((line) => {
                    const prevLine = this.lines.find((prevLine) => prevLine.lineId === line.lineId);
                    prevLine?.update(line);
                    return prevLine || new MiniCartLine(null, this.context, line);
                });

        this.renderAddedLines();
        await this.removeDeletedLines(deletedLines);
    }

    async updateLineState() {
        this.updateLineCount();
        await this.updateLines();

        if(store.state.rawLines.length) {
            this.showLines();
        } else {
            this.$lines.hide();
        }

        this.toggleEmptyCart();
    }

    updateLoadingState() {
        const loading = !!store.state.loadingLine;
        if(loading) {
            this.$price.empty();
        }
        this.priceLoader.active = loading;
    }

    updatePrice() {
        const price = store.state.miniCartTotalFormatted ? store.getters.formattedMiniCartTotal : this.context.EmptyCartPrice;
        this.$price.html(price);
    }
}
